import { tz } from "@date-fns/tz";
import { isValid } from "date-fns";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";

import { appStore } from "@src/shared/stores/app";

import { isDateOnlyString } from "./dates";
import { parsePositiveIntParam } from "./params";
import { getEffectiveTimezone } from "./timezone";

const SUPPORTED_FORMATS = [
  "yyyy-MM-dd",
  "MM/dd/yyyy",
  "MM-dd-yyyy",
  "M/d/yyyy",
  "M-d-yyyy",
  "dd/MM/yyyy",
  "dd-MM-yyyy",
  "MMMM d, yyyy", // June 1, 2025
  "MMM d, yyyy", // Jun 1, 2025
  "d MMMM yyyy", // 1 June 2025
];

export declare const brand: unique symbol;

export type Brand<T, B extends string> = T & { readonly [brand]: B };

export type NaiveDate = Brand<Date, "NaiveDate">;

export type TimezoneAwareDateString = Brand<string, "TimezoneAwareDateString">;

export type PracticeTimezoneAwareDateString = Brand<
  string,
  "PracticeTimezoneAwareDateString"
>;

/**
 * A timezone-agnostic date represented as midnight UTC.
 *
 * The invariant is: a `NaiveDate` is always a `Date` at `T00:00:00.000Z`.
 * Timezone information is discarded at construction time, not at render time.
 *
 * @example
 * const d = NaiveDate("2025-06-01");
 * const d = NaiveDate.fromISO("2025-06-01T00:30:00.000Z"); // => "2025-06-01"
 * const d = NaiveDate.fromDate(new Date());
 * NaiveDate.toString(d); // => "2025-06-01"
 */
export const NaiveDate = Object.assign(
  /**
   * Constructs a `NaiveDate` from a `yyyy-MM-dd` string.
   *
   * @param value - A date string in `yyyy-MM-dd` format.
   */
  (value: string): NaiveDate => {
    return new Date(`${value}T00:00:00.000Z`) as NaiveDate;
  },
  {
    fromString: (value: string): NaiveDate => {
      if (!value) throw new Error("NaiveDate.fromString: empty value");

      if (value.includes("T")) {
        return NaiveDate.fromISO(value);
      }

      for (const fmt of SUPPORTED_FORMATS) {
        const parsed = parse(value, fmt, new Date());

        if (isValid(parsed)) {
          return NaiveDate(format(parsed, "yyyy-MM-dd"));
        }
      }

      throw new Error(`NaiveDate.fromString: unrecognized format "${value}"`);
    },
    /**
     * Constructs a `NaiveDate` from a JS `Date`, using local date parts.
     * The time and timezone of the original `Date` are discarded.
     *
     * @param value - Any JS `Date` object.
     *
     * @example
     * // In UTC-5, this gives "2025-05-31", not "2025-06-01"
     * NaiveDate.fromDate(new Date("2025-06-01T00:30:00.000Z"));
     */
    fromDate: (value: Date): NaiveDate => {
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, "0");
      const dd = String(value.getDate()).padStart(2, "0");
      return new Date(`${yyyy}-${mm}-${dd}T00:00:00.000Z`) as NaiveDate;
    },

    /**
     * Constructs a `NaiveDate` from an ISO 8601 string.
     * Everything after the date portion (`yyyy-MM-dd`) is discarded.
     *
     * @param value - An ISO 8601 string, e.g. `"2025-06-01T00:30:00.000Z"`.
     *
     * @example
     * NaiveDate.fromISO("2025-06-01T00:30:00.000Z"); // => NaiveDate("2025-06-01")
     */
    fromISO: (value: string): NaiveDate => {
      return new Date(`${value.slice(0, 10)}T00:00:00.000Z`) as NaiveDate;
    },

    /**
     * Serializes a `NaiveDate` to a `yyyy-MM-dd` string.
     *
     * @param value - A `NaiveDate` instance.
     * @returns A date string in `yyyy-MM-dd` format.
     *
     * @example
     * NaiveDate.toString(NaiveDate("2025-06-01")); // => "2025-06-01"
     */
    toString: (value: NaiveDate): string => {
      return value.toISOString().slice(0, 10);
    },
  },
);

/**
 * Helpers for turning timestamps into **calendar dates** in a specific IANA timezone
 * (for example, “what day is it in Chicago?”).
 */
export const TimezoneAwareDateString = Object.assign(
  /**
   * Same as {@link TimezoneAwareDateString.toDisplay} with `yyyy-MM-dd`, branded for type safety.
   */
  (
    value: string | number | Date,
    timezone: string,
  ): TimezoneAwareDateString => {
    return TimezoneAwareDateString.toDisplay(
      value,
      timezone,
      "yyyy-MM-dd",
    ) as TimezoneAwareDateString;
  },
  {
    /**
     * Formats in `timezone`. Default is `MM/dd/yyyy`; pass `yyyy-MM-dd` for a date-only API string.
     *
     * For instants, the **clock in `timezone`** picks the calendar day.
     * For values that are already `yyyy-MM-dd` strings, we treat that as the literal day
     * (no zone shift on the string itself).
     */
    toDisplay: (
      value: string | number | Date,
      timezone: string,
      displayFormat = "MM/dd/yyyy",
    ): string => {
      if (typeof value === "string" && isDateOnlyString(value)) {
        // Treat as wall-clock midnight in the target timezone, not UTC or local
        return format(parse(value, "yyyy-MM-dd", new Date()), displayFormat);
      }

      return format(value, displayFormat, { in: tz(timezone) });
    },
  },
);

function getPracticeTimezone(): string {
  const { practiceTimezone, userTimezone } = appStore.getState();
  return getEffectiveTimezone(practiceTimezone, userTimezone);
}

/**
 * Like {@link TimezoneAwareDateString}, but the zone comes from the app store
 * (practice timezone when set, otherwise the user’s timezone).
 */
export const PracticeTimezoneAwareDateString = Object.assign(
  /**
   * Date-only `yyyy-MM-dd` in the **effective** practice/user timezone, branded for typing.
   */
  (value: string | number | Date): PracticeTimezoneAwareDateString => {
    return TimezoneAwareDateString(
      value,
      getPracticeTimezone(),
    ) as unknown as PracticeTimezoneAwareDateString;
  },
  {
    /**
     * Formats using the **effective** timezone from the app store; same rules as
     * {@link TimezoneAwareDateString.toDisplay} (default `MM/dd/yyyy`, or `yyyy-MM-dd` when you need ISO dates).
     */
    toDisplay: (
      value: string | number | Date,
      displayFormat = "MM/dd/yyyy",
    ): string => {
      return TimezoneAwareDateString.toDisplay(
        value,
        getPracticeTimezone(),
        displayFormat,
      );
    },
  },
);

export type PositiveInt = Brand<number, "PositiveInt">;

export const PositiveInt = Object.assign(
  (value: number): PositiveInt => {
    if (value <= 0) {
      throw new Error("PositiveInt: value must be positive");
    }

    if (!Number.isInteger(value)) {
      throw new Error("PositiveInt: value must be an integer");
    }

    return value as PositiveInt;
  },

  {
    parse: (value: string | number | null | undefined): PositiveInt | null => {
      const n = parsePositiveIntParam(value);

      if (n !== undefined) return n as PositiveInt;

      return null;
    },
  },
);
